import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../config/axios';

interface Sale {
  sale_id: number;
  branch_id: number;
  branch_name: string;
  receipt_number: string;
  total_amount: number;
  notes: string;
  sale_date: string;
  payment_method: string;
  sale_items: SaleItem[];
}

interface SaleItem {
  sale_item_id: number;
  sale_id: number;
  medicine_id: number;
  medicine_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  discount_percentage?: number;
  patient_name?: string | null;
  patient_id?: string | null;
}

interface SalesState {
  sales: Sale[];
  loading: boolean;
  error: string | null;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

const initialState: SalesState = {
  sales: [],
  loading: false,
  error: null,
  pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },
};

export const fetchSales = createAsyncThunk(
  'sales/fetchSales',
  async (params: { page?: number; limit?: number; search?: string; start_date?: string; end_date?: string; branchId?: number; payment_method?: string; payment_methods?: string[] } = {}) => {
    const { page = 1, limit = 10, search = '', start_date = '', end_date = '', branchId, payment_method, payment_methods } = params;
    
    console.log('fetchSales called with params:', { page, limit, search, start_date, end_date, branchId });
    
    const queryParams: any = { 
      page, 
      pageSize: limit, 
      search, 
      sale_from: start_date, 
      sale_to: end_date, 
      'populate[branch]': '*',
      'populate[created_by]': '*',
      'populate[sale_payments]': '*'
    };
    if (branchId) {
      queryParams['branch_id'] = branchId;
      console.log('Added branch_id filter:', branchId);
    } else {
      console.log('No branchId provided, fetching all sales');
    }
    if (payment_method) {
      queryParams['payment_method'] = payment_method;
    }
    if (payment_methods && payment_methods.length > 0) {
      // backend supports repeated params or comma-separated; we will send comma-separated here
      queryParams['payment_methods'] = payment_methods.join(',');
    }
    
    const response = await apiClient.get('/sales', { params: queryParams });
    
    // Fetch all sale items at once instead of individual calls
    let allSaleItems: any[] = [];
    try {
      const saleItemsResponse = await apiClient.get('/sale-items?pageSize=1000&populate[branch_medicine][populate][medicine]=*&populate[sale][fields][0]=id');
      allSaleItems = saleItemsResponse.data.data || [];
    } catch (error) {
      console.error('Error fetching sale items:', error);
    }
    
    // Group sale items by sale ID for efficient lookup
    const saleItemsBySaleId = allSaleItems.reduce((acc: any, item: any) => {
      const saleId = item.attributes?.sale?.data?.id;
      if (saleId) {
        if (!acc[saleId]) {
          acc[saleId] = [];
        }
        acc[saleId].push(item);
      }
      return acc;
    }, {});
    
    // Map sales with their corresponding sale items
    const salesWithItems = response.data.data.map((sale: any) => ({
      ...sale,
      attributes: {
        ...sale.attributes,
        sale_items: {
          data: saleItemsBySaleId[sale.id] || []
        }
      }
    }));
    
    return {
      ...response.data,
      data: salesWithItems
    };
  }
);

export const createSale = createAsyncThunk(
  'sales/createSale',
  async (saleData: {
    branch_id: number;
    receipt_number: string;
    notes: string;
    payment_method: string;
    items: Array<{
      medicine_id: number;
      quantity: number;
      unit_price: number;
    }>;
  }) => {
    const response = await apiClient.post('/sales', saleData);
    // Fetch the created sale with populated relationships
    const populatedResponse = await apiClient.get(`/sales/${response.data.data.id}?populate[sale_items][populate][branch_medicine][populate][medicine]=*&populate[branch]=*&populate[created_by]=*`);
    return populatedResponse.data;
  }
);

const salesSlice = createSlice({
  name: 'sales',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSales.fulfilled, (state, action) => {
        state.loading = false;
        // Transform the API response data to match the expected structure
        const transformedSales = Array.isArray(action.payload.data) 
          ? action.payload.data.map((sale: any) => ({
              sale_id: sale.id,
              receipt_number: sale.attributes?.receipt_number || '',
              total_amount: sale.attributes?.total_amount || 0,
              notes: sale.attributes?.notes || '',
              sale_date: sale.attributes?.sale_date || '',
              createdAt: sale.attributes?.createdAt || '',
              payment_method: sale.attributes?.payment_status || 'cash',
              payment_methods: (sale.attributes?.sale_payments?.data || []).map((payment: any) => ({
                payment_method: payment.attributes?.payment_method || 'cash',
                amount: payment.attributes?.amount || 0
              })),
              consultation_fee: sale.attributes?.consultation_fee || 0,
              discount_amount: sale.attributes?.discount_amount || 0,
              sub_total: sale.attributes?.sub_total || 0,
              branch_id: sale.attributes?.branch?.data?.id || 0,
              branch_name: sale.attributes?.branch?.data?.attributes?.branch_name || 'Unknown Branch',
              patient_name: sale.attributes?.patient_name || '',
              patient_id: sale.attributes?.patient_id || '',
              sale_items: (sale.attributes?.sale_items?.data || []).map((item: any) => ({
                sale_item_id: item.id,
                sale_id: sale.id,
                medicine_id: item.attributes?.branch_medicine?.data?.attributes?.medicine?.data?.id || 0,
                medicine_name: item.attributes?.branch_medicine?.data?.attributes?.medicine?.data?.attributes?.medicine_name || 'Unknown Medicine',
                quantity: item.attributes?.quantity || 0,
                unit_price: item.attributes?.unit_price || 0,
                total_price: item.attributes?.total_price || 0,
                discount_percentage: item.attributes?.discount_percentage || 0
              }))
            }))
          : [];
        state.sales = transformedSales;
        state.pagination = {
          current: action.payload.meta?.pagination?.page || 1,
          pageSize: action.payload.meta?.pagination?.pageSize || 10,
          total: action.payload.meta?.pagination?.total || 0,
          totalPages: action.payload.meta?.pagination?.pageCount || 0,
        };
      })
      .addCase(fetchSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch sales';
      })
      .addCase(createSale.fulfilled, (state, action) => {
        if (action.payload.data) {
          const sale = action.payload.data;
          const transformedSale = {
            sale_id: sale.id,
            receipt_number: sale.attributes?.receipt_number || '',
            total_amount: sale.attributes?.total_amount || 0,
            notes: sale.attributes?.notes || '',
            sale_date: sale.attributes?.sale_date || '',
            createdAt: sale.attributes?.createdAt || '',
            payment_method: sale.attributes?.payment_status || 'cash',
            payment_methods: (sale.attributes?.sale_payments?.data || []).map((payment: any) => ({
              payment_method: payment.attributes?.payment_method || 'cash',
              amount: payment.attributes?.amount || 0
            })),
            consultation_fee: sale.attributes?.consultation_fee || 0,
            discount_amount: sale.attributes?.discount_amount || 0,
            sub_total: sale.attributes?.sub_total || 0,
            branch_id: sale.attributes?.branch?.data?.id || 0,
            branch_name: sale.attributes?.branch?.data?.attributes?.branch_name || 'Unknown Branch',
            patient_name: sale.attributes?.patient_name || '',
            patient_id: sale.attributes?.patient_id || '',
            sale_items: (sale.attributes?.sale_items?.data || []).map((item: any) => ({
              sale_item_id: item.id,
              sale_id: sale.id,
              medicine_id: item.attributes?.branch_medicine?.data?.attributes?.medicine?.data?.id || 0,
              medicine_name: item.attributes?.branch_medicine?.data?.attributes?.medicine?.data?.attributes?.medicine_name || 'Unknown Medicine',
              quantity: item.attributes?.quantity || 0,
              unit_price: item.attributes?.unit_price || 0,
              total_price: item.attributes?.total_price || 0,
              discount_percentage: item.attributes?.discount_percentage || 0,
              // Extract patient information from sale object with null checks
              patient_name: item.attributes?.sale?.data?.attributes?.patient_name || null,
              patient_id: item.attributes?.sale?.data?.attributes?.patient_id || null
            }))
          };
          state.sales.unshift(transformedSale);
        }
      });
  },
});

export default salesSlice.reducer;
